/**
 * Pure view helpers for the Phase 2 split workspace (kanban 12584b08): how a
 * URL resolves to a thread or a case, where each thing links, which signals
 * are NEW since the reader last looked, and the Cases-tab filters. Kept out
 * of the components so the routing and marker math are testable without a
 * DOM (repo convention: logic in .ts, components in .tsx).
 */
import { isWaitingAsk } from '../../components/quality/askKind';
import type {
  DoorInvestigationRead,
  DoorThreadRead,
  DoorThreadSignalRead,
} from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { applyFilter, type InvestigationFilter } from '../../components/portal/investigationsView';
import { applyCourtFilter, type CourtFilter } from '../../components/portal/caseCourtView';

/* ---------------------------------------------------------------- */
/* Real URLs (deep links)                                            */
/* ---------------------------------------------------------------- */

/**
 * The workspace path for a thread, relative to the DOOR: `threads/QT-10007`.
 * Navigate to it as-is from the door's index route (Triage), and as
 * `../threads/…` from a sibling leaf tab (Cases / Routed away / Done) —
 * React Router resolves relative paths by route level.
 */
export function threadWorkspacePath(
  thread: Pick<DoorThreadRead, 'thread_no'>,
  from: 'index' | 'tab' = 'index',
): string {
  const path = `threads/${encodeURIComponent(thread.thread_no)}`;
  return from === 'tab' ? `../${path}` : path;
}

/** The workspace path for a case, relative to the door: `cases/INV-00007`
 *  (same `from` rule as threadWorkspacePath). */
export function caseWorkspacePath(
  investigation: Pick<DoorInvestigationRead, 'investigation_no'>,
  from: 'index' | 'tab' = 'index',
): string {
  const path = `cases/${encodeURIComponent(investigation.investigation_no)}`;
  return from === 'tab' ? `../${path}` : path;
}

/**
 * Resolve a URL segment to a thread. Accepts the human number (QT-10007,
 * what the door's own links use) OR the row id (what older notifications
 * and API-shaped links carry). Case-insensitive on the number.
 */
export function resolveThread(
  threads: DoorThreadRead[],
  ref: string | undefined,
): DoorThreadRead | null {
  const key = (ref ?? '').trim();
  if (!key) return null;
  const upper = key.toUpperCase();
  return (
    threads.find((t) => t.thread_no.toUpperCase() === upper) ??
    threads.find((t) => t.id === key) ??
    null
  );
}

/** Same for a case: INV-00007 or the investigation uuid. */
export function resolveCase(
  investigations: DoorInvestigationRead[],
  ref: string | undefined,
): DoorInvestigationRead | null {
  const key = (ref ?? '').trim();
  if (!key) return null;
  const upper = key.toUpperCase();
  return (
    investigations.find((i) => i.investigation_no.toUpperCase() === upper) ??
    investigations.find((i) => i.id === key) ??
    null
  );
}

/** The thread a case was opened from (birth-linked), when there is one. */
export function threadForCase(
  threads: DoorThreadRead[],
  investigation: Pick<DoorInvestigationRead, 'id'>,
): DoorThreadRead | null {
  return threads.find((t) => t.investigation_id === investigation.id) ?? null;
}

/** The case a thread was promoted into, when the case list has caught up. */
export function caseForThread(
  investigations: DoorInvestigationRead[],
  thread: Pick<DoorThreadRead, 'investigation_id'>,
): DoorInvestigationRead | null {
  if (!thread.investigation_id) return null;
  return investigations.find((i) => i.id === thread.investigation_id) ?? null;
}

/**
 * Where "← Back" lands: the tab the item lives on. A thread follows its
 * status (Triage / Routed away / Done); a case is the Cases tab. An item
 * waiting on someone is still one of the reader's own and lives on Triage
 * (waiting filter round: the "Waiting on others" tab is gone; it is a filter
 * on the lists now).
 */
/** Where an item was opened from. `drafts` is the Drafts to send list
 *  (UX round 2026-10-20) and `pending` the follow-ups list (kanban
 *  43160d8b): lists an item is opened from, not tabs an item lives on. */
export type WorkspaceOrigin = 'triage' | 'routed' | 'done' | 'cases' | 'drafts' | 'pending';

export function threadOrigin(
  thread: Pick<DoorThreadRead, 'status' | 'routed_to'>,
  isMine: boolean,
): WorkspaceOrigin {
  if (thread.status === 'in_investigation') return 'cases';
  if (thread.status === 'completed' || thread.status === 'dismissed') return 'done';
  return isMine ? 'triage' : 'routed';
}

/**
 * The list tabs open a workspace with `{ state: { from: <tab> } }`, so the
 * way back (and the highlighted tab) is the tab the reader came from: a
 * completed case opened from Done used to say "← Cases" and light up Cases
 * (fan-out finding 12). Without state (a pasted link) the item's own status
 * decides, as before.
 */
export interface WorkspaceNavState {
  from: WorkspaceOrigin;
  /** The item numbers the list showed, in its order — the workspace's
   *  previous / next walk it (case round, finding 6). */
  order?: string[];
  /** The list's filter query (`?show=waiting&who=…`), so "← Back" returns to
   *  the list as it was filtered. */
  search?: string;
}

const ORIGINS: readonly WorkspaceOrigin[] = ['triage', 'routed', 'done', 'cases', 'drafts', 'pending'];

export function originFromState(state: unknown): WorkspaceOrigin | null {
  const from = (state as { from?: unknown } | null)?.from;
  // History entries written while the Waiting on others tab existed: those
  // items live on Triage now.
  if (from === 'waiting') return 'triage';
  return typeof from === 'string' && (ORIGINS as readonly string[]).includes(from)
    ? (from as WorkspaceOrigin)
    : null;
}

/** The list's filter query carried in the state, when there is one. */
export function searchFromState(state: unknown): string {
  const search = (state as { search?: unknown } | null)?.search;
  return typeof search === 'string' && /^\?./.test(search) ? search : '';
}

/** The state a list tab passes when it opens a workspace: where it came
 *  from and, when given, the order of the items the list shows and the
 *  list's filter query. */
export function fromTab(
  origin: WorkspaceOrigin,
  order?: string[],
  search?: string,
): { state: WorkspaceNavState } {
  const state: WorkspaceNavState = { from: origin };
  if (order && order.length > 0) state.order = order;
  if (search && /^\?./.test(search)) state.search = search;
  return { state };
}

/** True on a workspace URL (`…/threads/QT-…` or `…/cases/INV-…`). */
export function isWorkspacePath(pathname: string): boolean {
  return /\/quality-copilot\/(threads|cases)\/[^/]+\/?$/.test(pathname);
}

export const ORIGIN_LABEL: Record<WorkspaceOrigin, string> = {
  triage: 'Triage',
  routed: 'Routed away',
  done: 'Done',
  cases: 'Cases',
  drafts: 'Drafts to send',
  pending: 'Pending',
};

/**
 * The origin tab's path FROM the workspace route (`threads/:ref` /
 * `cases/:ref` sit one route level below the door, so `..` is the door).
 * React Router resolves relative paths by route level, not URL segment —
 * the Phase 1 `../briefs` 404 came from forgetting that on the index route.
 */
export const ORIGIN_PATH: Record<WorkspaceOrigin, string> = {
  triage: '..',
  routed: '../routed',
  done: '../done',
  cases: '../cases',
  drafts: '../drafts',
  pending: '../pending',
};

/* ---------------------------------------------------------------- */
/* NEW markers on the evidence                                       */
/* ---------------------------------------------------------------- */

/**
 * A signal is NEW when it landed after the reader last read this thread
 * (the seen-mark captured BEFORE this visit marks it read). A thread never
 * read before shows no markers — everything would be new, which says
 * nothing.
 */
export function isNewSignal(
  signal: Pick<DoorThreadSignalRead, 'created_at'>,
  seenBefore: string | null | undefined,
): boolean {
  if (!seenBefore) return false;
  const seenAt = new Date(seenBefore).getTime();
  const landed = new Date(signal.created_at).getTime();
  if (Number.isNaN(seenAt) || Number.isNaN(landed)) return false;
  return landed > seenAt;
}

export function newSignalCount(
  signals: ReadonlyArray<Pick<DoorThreadSignalRead, 'created_at'>>,
  seenBefore: string | null | undefined,
): number {
  return signals.filter((s) => isNewSignal(s, seenBefore)).length;
}

/* ---------------------------------------------------------------- */
/* Asks that belong to a case / a thread                             */
/* ---------------------------------------------------------------- */

/**
 * The data requests that belong on a case's workspace: birth-stamped to the
 * case, or asked from one of its conversations (older rows predate the
 * investigation_id stamp), or asked from the thread's own conversation.
 */
export function asksForCase(
  requests: DoorDataRequestRead[],
  investigation: Pick<
    DoorInvestigationRead,
    'id' | 'conversation_ids' | 'investigation_no' | 'bridge_keys'
  >,
  thread: (Pick<DoorThreadRead, 'conversation_id'> & { id?: string }) | null,
): DoorDataRequestRead[] {
  const conversationIds = new Set<string>(investigation.conversation_ids);
  if (thread?.conversation_id) conversationIds.add(thread.conversation_id);
  return requests.filter(
    (r) =>
      r.investigation_id === investigation.id ||
      // Born on the thread's Do next (kanban d3ec3001).
      (!!thread?.id && !!r.thread_id && r.thread_id === thread.id) ||
      (!!r.conversation_id && conversationIds.has(r.conversation_id)) ||
      // An ask Merlin sent from the Ask tab carries no case stamp; it still
      // belongs here when it names the case or one of its lots (iteration 6:
      // the asks to Jake and Cliff about INV-00007 were missing from it).
      (!r.investigation_id && askNamesCase(r, investigation)),
  );
}

/** The lot / batch numbers in a case's keys ("lot", "lots", "batch" …). */
function caseLots(bridgeKeys: Record<string, string> | undefined): string[] {
  return Object.entries(bridgeKeys ?? {})
    .filter(([k]) => /lot|batch/i.test(k))
    .flatMap(([, v]) => v.split(/[\s,;/]+/))
    .map((v) => v.trim())
    .filter((v) => /^[A-Za-z0-9-]{5,}$/.test(v) && /\d/.test(v));
}

/** Does the ask's text name this case's number or one of its lots? */
export function askNamesCase(
  request: Pick<DoorDataRequestRead, 'question_title' | 'question_body' | 'context_summary'>,
  investigation: Pick<DoorInvestigationRead, 'investigation_no' | 'bridge_keys'>,
): boolean {
  const text = `${request.question_title}\n${request.question_body}\n${request.context_summary ?? ''}`;
  const escape = (v: string) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return [investigation.investigation_no, ...caseLots(investigation.bridge_keys)].some((term) =>
    new RegExp(`(^|[^A-Za-z0-9])${escape(term)}($|[^A-Za-z0-9])`, 'i').test(text),
  );
}

/**
 * The case an ask belongs to, if any: stamped to it, asked from one of its
 * conversations (or its thread's), or naming it. First match wins.
 */
export function caseForAsk(
  request: DoorDataRequestRead,
  investigations: DoorInvestigationRead[],
  threads: Pick<DoorThreadRead, 'conversation_id' | 'investigation_id'>[],
): DoorInvestigationRead | null {
  // Open cases before closed ones, so a lot shared with an old case does
  // not pull a new ask onto it.
  const ordered = [...investigations].sort(
    (a, b) => Number(!isOpenCase(a)) - Number(!isOpenCase(b)),
  );
  return (
    ordered.find((inv) => {
      const thread = threads.find((t) => t.investigation_id === inv.id) ?? null;
      return asksForCase([request], inv, thread).length > 0;
    }) ?? null
  );
}

function isOpenCase(inv: Pick<DoorInvestigationRead, 'status'>): boolean {
  return inv.status === 'investigating' || inv.status === 'remediating';
}

export interface WaitingRow {
  request: DoorDataRequestRead;
  investigation: DoorInvestigationRead | null;
}

/**
 * Everyone the door is waiting on, across cases: open asks (oldest first)
 * plus answers not yet read (on top). Asks on cancelled cases drop out.
 */
export function waitingOnPeople(
  requests: DoorDataRequestRead[],
  investigations: DoorInvestigationRead[],
  threads: Pick<DoorThreadRead, 'conversation_id' | 'investigation_id'>[],
): WaitingRow[] {
  const rows = requests
    .filter((r) => isWaitingAsk(r) || (r.status === 'answered' && r.answer_unseen))
    .map((request) => ({ request, investigation: caseForAsk(request, investigations, threads) }))
    .filter((row) => row.investigation?.status !== 'cancelled');
  const rank = (r: DoorDataRequestRead) => (r.status === 'answered' ? 0 : 1);
  return rows.sort(
    (a, b) =>
      rank(a.request) - rank(b.request) ||
      a.request.created_at.localeCompare(b.request.created_at),
  );
}

/** Who a case is waiting on: the people with an open ask on it, oldest first. */
export function waitingOnNames(asks: DoorDataRequestRead[]): string[] {
  const names: string[] = [];
  for (const r of [...asks].sort((a, b) => a.created_at.localeCompare(b.created_at))) {
    if (isWaitingAsk(r) && !names.includes(r.target_name)) names.push(r.target_name);
  }
  return names;
}

/** A thread with no case yet: the asks Merlin raised from its conversation. */
export function asksForThread(
  requests: DoorDataRequestRead[],
  thread: Pick<DoorThreadRead, 'id' | 'conversation_id'>,
): DoorDataRequestRead[] {
  // An ask born on the thread's Do next carries the thread itself (kanban
  // d3ec3001); one filed from Merlin's chat on the item carries the
  // conversation. Either belongs here.
  return requests.filter(
    (r) =>
      r.thread_id === thread.id ||
      (!!thread.conversation_id && r.conversation_id === thread.conversation_id),
  );
}

/* ---------------------------------------------------------------- */
/* Routed-away state line                                            */
/* ---------------------------------------------------------------- */

export type RoutedAwayActivity = 'quiet' | 'moved';

/**
 * What the Routed-away row can say honestly about the hand-off: nothing on
 * the thread has changed since it was sent ("quiet"), or something did
 * ("moved" — Merlin or the recipient touched it after the send).
 */
export function routedAwayActivity(
  thread: Pick<DoorThreadRead, 'routed_at' | 'updated_at'>,
): RoutedAwayActivity {
  if (!thread.routed_at) return 'quiet';
  const sent = new Date(thread.routed_at).getTime();
  const moved = new Date(thread.updated_at).getTime();
  if (Number.isNaN(sent) || Number.isNaN(moved)) return 'quiet';
  // A minute of slack: the reroute write itself bumps updated_at.
  return moved - sent > 60_000 ? 'moved' : 'quiet';
}

/** Days a hand-off may sit quiet before Routed away flags it. */
export const QUIET_TOO_LONG_DAYS = 7;

/**
 * Whole days a routed-away item has sat with nothing moving, when that is
 * QUIET_TOO_LONG_DAYS or more; null otherwise (turn 1: a 30-day silent
 * hand-off looked the same as a 14-minute-old one).
 */
export function quietDays(
  thread: Pick<DoorThreadRead, 'routed_at' | 'updated_at'>,
  now: Date = new Date(),
): number | null {
  if (!thread.routed_at || routedAwayActivity(thread) !== 'quiet') return null;
  const sent = new Date(thread.routed_at).getTime();
  if (Number.isNaN(sent)) return null;
  const days = Math.floor((now.getTime() - sent) / 86_400_000);
  return days >= QUIET_TOO_LONG_DAYS ? days : null;
}

/* ---------------------------------------------------------------- */
/* Cases tab filters (Scott, 2026-09-08: "filter by those things")   */
/* ---------------------------------------------------------------- */

export interface CaseFilters {
  /** Whose court (derived from the asks, proposal 5): the board's columns. */
  court: CourtFilter;
  /** Lifecycle stage. */
  stage: InvestigationFilter;
}

export const DEFAULT_CASE_FILTERS: CaseFilters = { court: 'any', stage: 'all' };

/**
 * Narrow the case list by court and stage. A court filter implies open
 * cases (a finished case is in nobody's court), so it and the Completed
 * stage cannot both match — that is the honest answer, not a bug.
 */
export function applyCaseFilters(
  investigations: DoorInvestigationRead[],
  filters: CaseFilters,
): DoorInvestigationRead[] {
  return applyCourtFilter(applyFilter(investigations, filters.stage), filters.court);
}

export function caseFiltersActive(filters: CaseFilters): boolean {
  return filters.court !== 'any' || filters.stage !== 'all';
}
